import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { Icon } from '@/components/ui/Icon'
import { cn } from '@/lib/cn'

type FieldSize = 'sm' | 'md' | 'lg' | 'xl'

const fieldSizeClasses: Record<FieldSize, string> = {
  sm: 'h-field-sm px-3 text-sm',
  md: 'h-field-md px-3 text-sm',
  lg: 'h-field-lg px-4 text-base',
  xl: 'h-field-xl px-4 text-base',
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'children' | 'size'> & {
  label: string
  hint?: string
  error?: string
  trailing?: ReactNode
  size?: FieldSize
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, trailing, className, required, disabled, size = 'lg', ...rest },
  ref,
) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ')

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-error-strong">
            {' *'}
          </span>
        ) : null}
      </label>

      <div className="relative flex items-center">
        <input
          ref={ref}
          id={id}
          required={required}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy === '' ? undefined : describedBy}
          className={cn(
            'w-full rounded-lg border bg-surface text-ink',
            fieldSizeClasses[size],
            'placeholder:text-ink-subtle',
            'transition-colors duration-250 ease-brand',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
            'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-surface-muted disabled:text-ink-disabled',
            error
              ? 'border-error-strong'
              : 'border-border hover:border-border-strong focus:border-brand-500',
            trailing ? 'pr-11' : null,
            className,
          )}
          {...rest}
        />
        {trailing ? <div className="absolute right-2 flex items-center">{trailing}</div> : null}
      </div>

      {hint ? (
        <p id={hintId} className="text-sm text-ink-muted">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="flex items-center gap-1.5 text-sm text-error-strong">
          <Icon name="alertTriangle" className="size-4" />
          {error}
        </p>
      ) : null}
    </div>
  )
})
